import React from "react";
import { Lock, Plus, Trash2 } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import { MAX_EXTRA_KEYS, PAYLOAD_TYPE_LABEL, PAYLOAD_TYPE_OPTIONS, REQUIRED_PAYLOAD } from "../features/domain-event.constants";

const inputClass = "h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-text_primary outline-none focus:border-border_strong";

// The payload keys of a domain event: the required ones (locked) plus extra keys the event sends.
// `value` = [{ key, label, data_type }] (extra keys only).
export default function PayloadKeysEditor({ value, onChange, hasError }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (index, change) => onChange(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-border rounded-lg border border-border bg-surface_alt">
        {REQUIRED_PAYLOAD.map((item) => (
          <li key={item.key} className="flex items-center gap-2 px-3 py-2 text-xs">
            <Lock size={12} className="shrink-0 text-text_muted" aria-label="Always included" />
            <span className="w-28 shrink-0 font-mono text-text_primary">{item.key}</span>
            <span className="min-w-0 flex-1 truncate text-text_muted">{item.label}</span>
            <span className="shrink-0 text-text_muted">{PAYLOAD_TYPE_LABEL[item.data_type]}</span>
          </li>
        ))}
      </ul>

      {rows.length === 0 ? (
        <p className="text-[11px] text-text_muted">No extra keys. Add one when triggers need more than the record and the subject, e.g. the new status.</p>
      ) : (
        rows.map((row, index) => (
          <div key={index} className={`grid grid-cols-1 items-center gap-2 rounded-lg border p-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_9rem_auto] ${hasError ? "border-danger_border" : "border-border"}`}>
            <input
              aria-label="Key"
              placeholder="key, e.g. new_status"
              value={row.key ?? ""}
              onChange={(e) => update(index, { key: e.target.value.toLowerCase() })}
              className={`${inputClass} font-mono`}
            />
            <input aria-label="Label" placeholder="Label (optional)" value={row.label ?? ""} onChange={(e) => update(index, { label: e.target.value })} className={inputClass} />
            <SearchableSelect compact ariaLabel="Data type" options={PAYLOAD_TYPE_OPTIONS} value={row.data_type || "TEXT"} onChange={(next) => update(index, { data_type: next || "TEXT" })} />
            <button
              type="button"
              aria-label="Remove key"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-danger_border bg-danger_bg text-danger"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))
      )}

      <button
        type="button"
        disabled={rows.length >= MAX_EXTRA_KEYS}
        onClick={() => onChange([...rows, { key: "", label: "", data_type: "TEXT" }])}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text_secondary disabled:opacity-50"
      >
        <Plus size={13} /> Add key
      </button>
    </div>
  );
}
